import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { domains, articles, categories } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug || a.id === slug);
  const domainDetail = domains.find((d) => d.id === slug);

  if (!article && !domainDetail) {
    return (
      <>
        <ScrollToTop />
        <main className="pt-24 md:pt-28 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2 font-heading">{t('article.noArticle')}</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
              {t('category.backToHome')}
            </Link>
          </div>
        </main>
      </>
    );
  }

  const relatedArticles = article
    ? articles.filter((a) => a.id !== article.id).slice(0, 3)
    : [];

  const relatedDomains = article?.relatedDomainIds
    ? domains.filter((d) => article.relatedDomainIds.includes(d.id))
    : [];

  const currentIdx = articles.findIndex((a) => a.id === (article?.id || ''));
  const prevArticle = currentIdx > 0 ? articles[currentIdx - 1] : null;
  const nextArticle = currentIdx < articles.length - 1 ? articles[currentIdx + 1] : null;

  const pageTitle = article?.title || domainDetail?.name || '';
  const pageDescription = article?.excerpt || domainDetail?.description || '';
  const pageUrl = `https://japanlink.github.io/article/${slug}`;

  // Schema.org Article
  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    url: pageUrl,
  } : null;

  const breadcrumbItems = article
    ? [
        { label: t('common.breadcrumbHome'), href: '/' },
        { label: t('nav.categories'), href: '/category/video' },
        { label: article.title },
      ]
    : [
        { label: t('common.breadcrumbHome'), href: '/' },
        { label: t('nav.categories'), href: '/category/video' },
        { label: categories.find((c) => c.id === domainDetail?.category)?.name || '', href: `/category/${domainDetail?.category}` },
        { label: domainDetail?.name || '' },
      ];

  return (
    <>
      <ScrollToTop />
      {articleSchema && <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>}

      <main className="pt-20 md:pt-24">
        <Breadcrumb items={breadcrumbItems} />

        {/* Domain Detail View */}
        {domainDetail && !article && (
          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/60">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-primary-100 flex-shrink-0">
                    <i className="ri-global-line text-primary-600 text-2xl"></i>
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2 font-heading">
                      {domainDetail.name}
                    </h1>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          domainDetail.status === 'online'
                            ? 'bg-accent-100 text-accent-700'
                            : 'bg-foreground-100 text-foreground-500'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            domainDetail.status === 'online' ? 'bg-accent-500' : 'bg-foreground-400'
                          }`}
                        ></span>
                        {domainDetail.status === 'online' ? t('common.online') : t('common.offline')}
                      </span>
                      <span className="text-xs text-foreground-400">
                        {t('article.visits')}：{(domainDetail.visits / 10000).toFixed(1)}万
                      </span>
                      <span className="text-xs text-foreground-400">
                        更新：{domainDetail.updateTime}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="text-sm font-semibold text-foreground-700 mb-2">域名地址</h2>
                  <div className="flex items-center gap-3 bg-background-100 rounded-lg px-4 py-3">
                    <code className="text-sm text-primary-600 flex-1 break-all">{domainDetail.url}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(domainDetail.url).then(() => {
                          alert(t('common.copySuccess'));
                        }).catch(() => alert(t('common.copyFailed')));
                      }}
                      className="px-3 py-1.5 bg-primary-500 text-background-50 rounded text-xs font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-file-copy-line mr-1"></i>复制
                    </button>
                    <a
                      href={domainDetail.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="px-3 py-1.5 bg-accent-500 text-background-50 rounded text-xs font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-external-link-line mr-1"></i>访问
                    </a>
                  </div>
                </div>

                <div>
                  <h2 className="text-sm font-semibold text-foreground-700 mb-2">域名介绍</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{domainDetail.description}</p>
                </div>

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-foreground-400">{t('article.tags')}：</span>
                  {domainDetail.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>
        )}

        {/* Article Detail View */}
        {article && (
          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-2xl p-6 md:p-10 border border-background-200/60">
                <header className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-2.5 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                      {categories.find((c) => c.id === article.category)?.name || article.category}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {t('article.publishDate')}：{article.publishDate}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {t('article.updateDate')}：{article.updateDate}
                    </span>
                  </div>

                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading leading-tight">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-4 text-xs text-foreground-400">
                    <span>
                      <i className="ri-user-line mr-1"></i>
                      {t('article.author')}：{article.author}
                    </span>
                    <span>
                      <i className="ri-eye-line mr-1"></i>
                      {t('article.visits')}：{(article.visits / 10000).toFixed(1)}万
                    </span>
                  </div>
                </header>

                <div
                  className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="mt-8 pt-6 border-t border-background-100">
                  <div className="flex items-center gap-2 flex-wrap mb-6">
                    <span className="text-xs text-foreground-400">{t('article.tags')}：</span>
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Related Domains */}
                  {relatedDomains.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-3 font-heading">{t('article.relatedDomains')}</h3>
                      <div className="flex flex-wrap gap-2">
                        {relatedDomains.map((d) => (
                          <Link
                            key={d.id}
                            to={`/article/${d.id}`}
                            className="flex items-center gap-2 px-3 py-2 bg-background-100 hover:bg-background-200 rounded-lg text-sm text-foreground-600 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <span className={`w-2 h-2 rounded-full ${d.status === 'online' ? 'bg-accent-500' : 'bg-foreground-400'}`}></span>
                            {d.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Prev/Next Navigation */}
                  <div className="flex flex-col sm:flex-row justify-between gap-3">
                    {prevArticle ? (
                      <Link
                        to={`/article/${prevArticle.id}`}
                        className="flex-1 flex items-center gap-2 p-3 bg-background-100 hover:bg-background-200 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-line text-foreground-400 flex-shrink-0"></i>
                        <span className="text-sm text-foreground-600 truncate">{prevArticle.title}</span>
                      </Link>
                    ) : <div className="flex-1"></div>}
                    {nextArticle ? (
                      <Link
                        to={`/article/${nextArticle.id}`}
                        className="flex-1 flex items-center justify-end gap-2 p-3 bg-background-100 hover:bg-background-200 rounded-lg transition-colors cursor-pointer"
                      >
                        <span className="text-sm text-foreground-600 truncate">{nextArticle.title}</span>
                        <i className="ri-arrow-right-line text-foreground-400 flex-shrink-0"></i>
                      </Link>
                    ) : <div className="flex-1"></div>}
                  </div>
                </div>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div className="mt-10">
                  <h2 className="text-lg font-semibold text-foreground-950 mb-4 font-heading">{t('article.relatedArticles')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="group bg-background-50 rounded-xl p-4 border border-background-200/60 hover:border-primary-200 transition-all duration-300 no-underline cursor-pointer"
                      >
                        <h3 className="text-sm font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2 font-heading">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        )}
      </main>
    </>
  );
}